/**
 * StatusDot — the replacement for every colored pill / badge.
 *
 * Resting states get a small dot with a muted label. Loud bg-X/15 pills
 * are removed everywhere; they trained the eye to scan colors that don't
 * mean anything. Reserved for actual state.
 *
 * Usage:
 *   <StatusDot status="ok"   label="done" />
 *   <StatusDot status="busy" label="thinking" />
 *
 * If you need just the dot (no label), pass `label={null}`.
 */
import type { ReactNode } from "react";

export type DotStatus = "neutral" | "ok" | "warn" | "err" | "busy";

interface StatusDotProps {
  status: DotStatus;
  label?: ReactNode;
  className?: string;
  title?: string;
}

const DOT_CLASS: Record<DotStatus, string> = {
  neutral: "ma-dot",
  ok:      "ma-dot ma-dot--ok",
  warn:    "ma-dot ma-dot--warn",
  err:     "ma-dot ma-dot--err",
  busy:    "ma-dot ma-dot--busy",
};

export function StatusDot({ status, label, className = "", title }: StatusDotProps) {
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      title={title}
      role={label ? undefined : "status"}
      aria-label={typeof label === "string" ? label : undefined}
    >
      <span className={DOT_CLASS[status]} />
      {label !== null && label !== undefined && (
        <span className="ma-micro" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text)" }}>
          {label}
        </span>
      )}
    </span>
  );
}
